import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, Star } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { ContentBlock, LabDetail, LabStep } from '../../types/content';
import AuthorCard from '../../components/content/AuthorCard';
import Button from '../../components/UI/Button';
import ContentBlocks from '../../components/content/ContentBlocks';
import CourseRating, { readCourseRating } from '../../components/content/CourseRating';
import { useAuth } from '../../context/AuthContext';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import { notifyLabCompleted } from '../../utils/labCompletion';
import MobileHamburgerNav from '../../components/content/MobileHamburgerNav';
import PublicLabCourseNote from '../../components/content/PublicLabCourseNote';

const labStepsFrom = (lab: LabDetail): LabStep[] => {
  if (lab.steps?.length) return [...lab.steps].sort((a, b) => a.displayOrder - b.displayOrder);
  return (lab.modules || []).flatMap((module) =>
    (module.topics || []).map((topic, index) => ({
      title: topic.title,
      instruction: topic.objective || topic.blocks?.map((block) => block.content).filter(Boolean).join('\n\n') || '',
      displayOrder: index + 1,
    }))
  );
};

const blocksWithoutDuplicateHeading = (blocks: ContentBlock[] | undefined, title: string): ContentBlock[] => {
  const list = blocks || [];
  const norm = title.trim().toLowerCase();
  return list.filter((block) => {
    if ((block.type || '').toLowerCase() !== 'heading') return true;
    return (block.content || '').trim().toLowerCase() !== norm;
  });
};

const LabDetailPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const catalogCourseSlug = params.get('course');
  const { isAuthenticated } = useAuth();
  const [lab, setLab] = useState<LabDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [labDone, setLabDone] = useState(false);
  const [progressCourseId, setProgressCourseId] = useState<number | null>(null);
  const [completing, setCompleting] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [contentLoading, setContentLoading] = useState(false);
  const [mobileStepsOpen, setMobileStepsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingNote, setRatingNote] = useState('');
  const [ratingMessage, setRatingMessage] = useState<string | null>(null);
  const stepsSectionRef = useRef<HTMLElement>(null);
  const skipStepScrollRef = useRef(true);

  useEffect(() => {
    if (skipStepScrollRef.current) {
      skipStepScrollRef.current = false;
      return;
    }
    stepsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [stepIndex]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setContentLoading(true);
    setError(null);
    contentApi.getLabBySlug(slug, catalogCourseSlug).then((r) => {
      if (cancelled) return;
      const loaded = r.data.lab;
      if (!loaded) {
        setError('This lab is not available.');
        return;
      }
      setLab(loaded);
      setStepIndex(0);
    }).catch(() => {
      if (!cancelled) setError('This lab is not available.');
    }).finally(() => {
      if (!cancelled) setContentLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug, catalogCourseSlug]);

  useEffect(() => {
    if (!lab) {
      setProgressCourseId(null);
      return;
    }
    const applyCourseId = (courseId: number) => {
      setProgressCourseId(courseId);
      contentApi.getCourseProgress(courseId).then((p) => {
        setLabDone(!!p.data.progress?.completedLabIds?.includes(lab.id));
      }).catch(() => undefined);
    };
    if (lab.courseId) {
      applyCourseId(lab.courseId);
      return;
    }
    const courseSlug = catalogCourseSlug || lab.courseSlug;
    if (!courseSlug) {
      setProgressCourseId(null);
      return;
    }
    contentApi.getCourseBySlug(courseSlug).then((r) => {
      if (r.data.course?.id) applyCourseId(r.data.course.id);
    }).catch(() => undefined);
  }, [catalogCourseSlug, lab]);

  if (error && !lab) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-gray-600">
        <p>{error}</p>
        {catalogCourseSlug ? (
          <p className="mt-4">
            <Link to={`/courses/${catalogCourseSlug}`} className="text-primary-700 font-medium">Back to the course</Link>
          </p>
        ) : null}
      </div>
    );
  }
  if (!lab) return <PageLoading label="Loading lab…" />;

  const courseSlug = catalogCourseSlug || lab.courseSlug || '';
  const steps = labStepsFrom(lab);
  const currentStep = steps[stepIndex];
  const isLastStep = steps.length === 0 || stepIndex === steps.length - 1;

  const markLabDone = () => {
    if (labDone || completing) return;
    if (!progressCourseId) {
      setLabDone(true);
      notifyLabCompleted(lab.id, null);
      return;
    }
    setCompleting(true);
    contentApi.completeLab(progressCourseId, lab.id)
      .then((response) => {
        setLabDone(!!response.data.progress?.completedLabIds?.includes(lab.id) || !!response.data.success);
        notifyLabCompleted(lab.id, progressCourseId);
      })
      .catch(() => undefined)
      .finally(() => setCompleting(false));
  };

  const completionStatus = labDone ? (
    <div className="flex flex-wrap items-center gap-3">
      <span className="inline-flex items-center gap-2 text-sm font-medium text-success-700">
        <CheckCircle className="h-4 w-4" /> Completed
      </span>
      <Button type="button" variant="outline" onClick={() => setStepIndex(0)}>Practice again</Button>
    </div>
  ) : (
    <Button type="button" onClick={markLabDone} disabled={completing}>Done</Button>
  );

  const ratingPanel = (
    <div className="max-w-xl rounded-2xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Rate this lab</p>
      <CourseRating
        average={readCourseRating(lab).average}
        count={readCourseRating(lab).count}
        className="text-sm text-amber-700"
        showEmpty
      />
      {isAuthenticated ? (
        <form
          className="mt-3 space-y-2"
          onSubmit={async (event) => {
            event.preventDefault();
            setRatingMessage(null);
            try {
              const response = await contentApi.rateLab(lab.id, rating, ratingNote);
              const next = readCourseRating(response.data);
              setLab((current) => current ? { ...current, averageRating: next.average, ratingCount: next.count } : current);
              setRatingMessage(response.data.message || 'Rating saved.');
            } catch (err: unknown) {
              setRatingMessage(err instanceof Error ? err.message : 'Could not save the rating.');
            }
          }}
        >
          <div className="flex items-center gap-0.5" role="radiogroup" aria-label="Lab rating">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                className="p-0.5 rounded text-amber-400"
                onMouseEnter={() => setHoverRating(value)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(value)}
              >
                <Star className={`h-6 w-6 ${value <= (hoverRating || rating) ? 'fill-current' : ''}`} strokeWidth={1.75} />
              </button>
            ))}
          </div>
          <input className="w-full rounded-md border-gray-300 text-sm" placeholder="Comment (optional)" value={ratingNote} onChange={(e) => setRatingNote(e.target.value)} />
          <Button type="submit" size="sm" disabled={!rating}>Save rating</Button>
          {ratingMessage && <p className="text-sm text-gray-600">{ratingMessage}</p>}
        </form>
      ) : (
        <p className="mt-2 text-sm text-gray-600">Sign in to rate this lab.</p>
      )}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative">
      {contentLoading && (
        <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[2px] flex items-start justify-center pt-24 text-sm text-gray-600">
          Loading lab…
        </div>
      )}
      <nav className="text-sm text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-1">
        {catalogCourseSlug ? (
          <Link to="/courses" className="text-primary-600 font-medium hover:text-primary-800">Courses</Link>
        ) : (
          <Link to="/labs" className="text-primary-600 font-medium hover:text-primary-800">Labs</Link>
        )}
        {lab.categoryName && (
          <>
            <span>/</span>
            <Link
              to={lab.categoryId ? `${catalogCourseSlug ? '/courses' : '/labs'}?category=${lab.categoryId}` : (catalogCourseSlug ? '/courses' : '/labs')}
              className="text-primary-600 font-medium hover:text-primary-800"
            >
              {lab.categoryName}
            </Link>
          </>
        )}
        {lab.courseTitle && (
          <>
            <span>/</span>
            {courseSlug ? (
              <Link to={`/courses/${courseSlug}`} className="text-primary-600 font-medium hover:text-primary-800">{lab.courseTitle}</Link>
            ) : (
              <span>{lab.courseTitle}</span>
            )}
          </>
        )}
        {lab.moduleTitle && (
          <>
            <span>/</span>
            <span>{lab.moduleTitle}</span>
          </>
        )}
        {lab.courseTopicTitle && (
          <>
            <span>/</span>
            {courseSlug && lab.courseTopicSlug ? (
              <Link to={`/courses/${courseSlug}/topics/${lab.courseTopicSlug}`} className="text-primary-600 font-medium hover:text-primary-800">{lab.courseTopicTitle}</Link>
            ) : (
              <span>{lab.courseTopicTitle}</span>
            )}
          </>
        )}
        <span>/</span>
        <span className="text-gray-800">{lab.name}</span>
      </nav>
      <h1 className="mt-4 text-4xl font-bold text-gray-900 inline-flex items-center gap-2">
        {lab.name}
        {labDone ? <CheckCircle className="h-7 w-7 text-success-600" aria-label="Completed" /> : null}
      </h1>
      <p className="mt-3 text-lg text-gray-600 max-w-3xl">{lab.subtitle || lab.description}</p>
      {!catalogCourseSlug && lab.isPublic && courseSlug ? (
        <PublicLabCourseNote className="mt-4 max-w-3xl" courseSlug={courseSlug} courseTitle={lab.courseTitle} />
      ) : null}
      <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-500">
        <span>{lab.difficulty}</span>
        <span>{lab.duration || 'Self-paced'}</span>
        <span>{steps.length ? `${steps.length} steps` : 'Practice lab'}</span>
      </div>
      {(lab.technologyName || lab.prerequisites || lab.learningOutcomes) ? (
        <div className="mt-6 max-w-3xl rounded-2xl border-l-4 border-primary-600 bg-primary-50 px-5 py-4 space-y-4">
          {lab.technologyName ? (
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-primary-800">Technology</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">{lab.technologyName}</p>
            </div>
          ) : null}
          {lab.prerequisites ? (
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-primary-800">Prerequisites</p>
              <p className="mt-1 text-base font-medium text-gray-900 whitespace-pre-wrap">{lab.prerequisites}</p>
            </div>
          ) : null}
          {lab.learningOutcomes ? (
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-primary-800">Outcomes</p>
              <p className="mt-1 text-base font-medium text-gray-900 whitespace-pre-wrap">{lab.learningOutcomes}</p>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="mt-6 max-w-xl"><AuthorCard author={lab.author} compact /></div>

      {lab.courseTopicSlug && courseSlug ? (
        <div className="mt-4">
          <Button
            type="button"
            className="!bg-primary-700 hover:!bg-primary-800 focus:!ring-primary-700"
            onClick={() => navigate(`/courses/${courseSlug}/topics/${lab.courseTopicSlug}`)}
          >
            Back to topic
          </Button>
        </div>
      ) : null}

      <section ref={stepsSectionRef} className="mt-10 scroll-mt-24">
        <h2 className="text-2xl font-bold text-gray-900">Practice steps</h2>
        <p className="mt-2 text-sm text-gray-600">This lab is one practice task. Complete the steps in order.</p>
        {steps.length === 0 && (
          <div className="mt-4 space-y-4">
            <p className="text-gray-500">Steps for this lab are being assembled.</p>
            {completionStatus}
          </div>
        )}
        {currentStep && (
          <div className="mt-5 grid lg:grid-cols-[16rem_minmax(0,1fr)] gap-6 items-start">
            <MobileHamburgerNav
              open={mobileStepsOpen}
              onToggle={() => setMobileStepsOpen((open) => !open)}
              label="steps"
            >
              <nav className="surface-card p-3" aria-label="Lab steps">
                <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Steps</p>
                <ol className="space-y-1">
                  {steps.map((step, index) => (
                    <li key={step.id || `${step.title}-${index}`}>
                      <button
                        type="button"
                        className={`w-full text-left rounded-lg px-3 py-2 text-sm ${
                          index === stepIndex ? 'bg-primary-50 text-primary-800 font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        onClick={() => { setStepIndex(index); setMobileStepsOpen(false); }}
                      >
                        {index + 1}. {step.title}
                      </button>
                    </li>
                  ))}
                </ol>
              </nav>
            </MobileHamburgerNav>
            <nav className="hidden lg:block lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto surface-card p-3" aria-label="Lab steps">
              <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Steps</p>
              <ol className="space-y-1">
                {steps.map((step, index) => (
                  <li key={step.id || `${step.title}-${index}`}>
                    <button
                      type="button"
                      className={`w-full text-left rounded-lg px-3 py-2 text-sm ${
                        index === stepIndex ? 'bg-primary-50 text-primary-800 font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      onClick={() => setStepIndex(index)}
                    >
                      {index + 1}. {step.title}
                    </button>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="min-w-0 surface-card p-5">
              <p className="text-xs uppercase tracking-wide text-gray-500">Step {stepIndex + 1} of {steps.length}</p>
              <h3 className="mt-2 text-xl font-semibold text-gray-900">{currentStep.title}</h3>
              {(currentStep.blocks && currentStep.blocks.length > 0) ? (
                <div className="mt-3">
                  <ContentBlocks blocks={blocksWithoutDuplicateHeading(currentStep.blocks, currentStep.title)} compact />
                </div>
              ) : currentStep.instruction ? (
                <p className="mt-3 text-sm text-gray-700 whitespace-pre-wrap">{currentStep.instruction}</p>
              ) : null}
              {currentStep.codeSample && (
                <pre className="mt-4 overflow-x-auto rounded-xl bg-gray-900 text-gray-100 p-4 text-sm">
                  <code>{currentStep.codeSample}</code>
                </pre>
              )}
              {currentStep.expectedResult && (
                <p className="mt-4 text-sm text-gray-700"><span className="font-medium">Expected result: </span>{currentStep.expectedResult}</p>
              )}
              {currentStep.notes && <p className="mt-2 text-sm text-gray-600">{currentStep.notes}</p>}
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" disabled={stepIndex === 0} onClick={() => setStepIndex((current) => Math.max(0, current - 1))}>
                  Previous step
                </Button>
                {!isLastStep ? (
                  <Button type="button" onClick={() => setStepIndex((current) => Math.min(steps.length - 1, current + 1))}>
                    Next step
                  </Button>
                ) : completionStatus}
              </div>
            </div>
          </div>
        )}
      </section>
      <div className="mt-10">{ratingPanel}</div>
    </div>
  );
};

export default LabDetailPage;
